import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import type { Conversation } from '@tips-crm/shared';
import {
  assignContact,
  fetchAssignableUsers,
  fetchContact,
  fetchContactConversations,
  startOutboundCall,
} from '../api/endpoints.js';
import { errorMessage } from '../api/client.js';
import { useAuth } from '../auth/AuthContext.js';
import { PageHeader } from '../components/PageHeader.js';
import { DataTable, type Column } from '../components/DataTable.js';
import { CallPrepCard } from '../components/CallPrepCard.js';
import { ErrorState, Loading } from '../components/States.js';

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

export function ContactDetailPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { hasRole } = useAuth();
  const isAdmin = hasRole('platform_admin', 'reseller_admin');

  const contactQ = useQuery({
    queryKey: ['contact', id],
    queryFn: () => fetchContact(id),
    enabled: Boolean(id),
  });

  // Reps an admin can allocate this lead to (admin-only endpoint).
  const usersQ = useQuery({
    queryKey: ['assignable-users'],
    queryFn: fetchAssignableUsers,
    enabled: isAdmin,
  });

  const assignMut = useMutation({
    mutationFn: (assignedUserId: string | null) => assignContact(id, assignedUserId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['contact', id] });
      void queryClient.invalidateQueries({ queryKey: ['contacts'] });
    },
  });

  const convQ = useQuery({
    queryKey: ['contact-conversations', id],
    queryFn: () => fetchContactConversations(id),
    enabled: Boolean(id),
  });

  // Click-to-call: places an attributed outbound call (this rep + this contact),
  // then jumps to the workspace where the softphone + live call card live.
  const callMut = useMutation({
    mutationFn: () => startOutboundCall({ contact_id: id }),
    onSuccess: (res) => {
      void queryClient.invalidateQueries({ queryKey: ['contact-conversations', id] });
      if (res.connect.dormant) {
        window.alert(
          'Calling is not enabled in this environment yet (Amazon Connect is dormant). The call was logged but not dialed.',
        );
      } else {
        navigate('/workspace');
      }
    },
  });

  const columns = useMemo<Column<Conversation>[]>(
    () => [
      {
        key: 'channel',
        header: 'Channel',
        render: (c) => <span className={`badge badge--${c.channel}`}>{c.channel}</span>,
      },
      { key: 'direction', header: 'Direction', render: (c) => c.direction },
      {
        key: 'status',
        header: 'Status',
        render: (c) => <span className={`badge badge--status-${c.status}`}>{c.status}</span>,
      },
      {
        key: 'disposition',
        header: 'Disposition',
        render: (c) =>
          c.disposition ? (
            <>
              {c.disposition.replace(/_/g, ' ')}
              {c.disposition_source === 'ai' && <span className="badge badge--note">AI</span>}
            </>
          ) : (
            '—'
          ),
      },
      { key: 'started', header: 'Started', render: (c) => formatDate(c.started_at) },
    ],
    [],
  );

  if (contactQ.isLoading) return <Loading />;
  if (contactQ.isError) return <ErrorState error={contactQ.error} onRetry={() => contactQ.refetch()} />;

  const contact = contactQ.data;
  const name =
    contact && (contact.first_name || contact.last_name)
      ? [contact.first_name, contact.last_name].filter(Boolean).join(' ')
      : contact?.email || contact?.phone_e164 || 'Contact';

  return (
    <div className="page">
      <PageHeader
        title={name}
        subtitle="Contact detail"
        actions={
          <>
            {contact?.phone_e164 && (
              <button
                type="button"
                className="btn btn--primary"
                disabled={callMut.isPending}
                onClick={() => callMut.mutate()}
                title={`Call ${contact.phone_e164}`}
              >
                {callMut.isPending ? 'Calling…' : `📞 Call`}
              </button>
            )}
            <Link to="/contacts" className="btn btn--ghost">
              ← Back to contacts
            </Link>
          </>
        }
      />
      {callMut.isError && <div className="notice notice--error">{errorMessage(callMut.error)}</div>}

      {contact && (
        <section className="card">
          <h2 className="card__title">Details</h2>
          <dl className="kv kv--columns">
            <dt>Email</dt>
            <dd>{contact.email ?? '—'}</dd>
            <dt>Phone</dt>
            <dd>
              {contact.phone_e164 ? (
                <button
                  type="button"
                  className="linklike"
                  disabled={callMut.isPending}
                  onClick={() => callMut.mutate()}
                >
                  {contact.phone_e164}
                </button>
              ) : (
                '—'
              )}
            </dd>
            <dt>Company</dt>
            <dd>{contact.company ?? '—'}</dd>
            <dt>Lifecycle</dt>
            <dd>{contact.lifecycle_status ?? '—'}</dd>
            <dt>External ID</dt>
            <dd>{contact.external_customer_id ?? '—'}</dd>
            <dt>Owner</dt>
            <dd>
              {isAdmin ? (
                <select
                  className="field__input"
                  value={contact.assigned_user_id ?? ''}
                  disabled={assignMut.isPending || usersQ.isLoading}
                  onChange={(e) => assignMut.mutate(e.target.value || null)}
                >
                  <option value="">Unassigned</option>
                  {(usersQ.data ?? []).map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.display_name} ({u.email})
                    </option>
                  ))}
                </select>
              ) : (
                contact.assigned_user_id ? 'Assigned to you' : '—'
              )}
              {assignMut.isError && (
                <span className="form-error"> {errorMessage(assignMut.error)}</span>
              )}
            </dd>
            <dt>Created</dt>
            <dd>{formatDate(contact.created_at)}</dd>
          </dl>
        </section>
      )}

      <CallPrepCard contactId={id} />

      <section className="card">
        <h2 className="card__title">Conversations</h2>
        <DataTable
          columns={columns}
          rows={convQ.data}
          rowKey={(c) => c.id}
          isLoading={convQ.isLoading}
          isError={convQ.isError}
          error={convQ.error}
          onRetry={() => convQ.refetch()}
          onRowClick={(c) => navigate(`/conversations/${c.id}`)}
          emptyMessage="No conversations for this contact yet."
        />
      </section>
    </div>
  );
}
